import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useInView, animate } from "framer-motion";
import { ArrowRight, ArrowDown, ShieldCheck, Globe2, BadgePercent, ClipboardCheck, Phone, FileText, Handshake } from "lucide-react";
import api from "../api";
import Seo from "../components/Seo";
import { useLang, Link } from "../i18n";
import Marquee from "../components/Marquee";
import VehicleCard from "../components/VehicleCard";
import Reveal from "../components/Reveal";

const HERO_BG = "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const HERO_CARD = "/cayenne-hero.jpg";

const CATEGORIES = [
  { key: "cars", img: "https://kataco.ro/wp-content/uploads/2026/09/WhatsApp-Image-2026-09-08-at-16.05.00-1.jpeg" },
  { key: "vans", img: "https://kataco.ro/wp-content/uploads/2022/04/3955073_mercedes-benz-sprinter-309_2.jpg" },
  { key: "trucks", img: "https://kataco.ro/wp-content/uploads/2022/05/3945953_mercedes-benz-atego-1823_8.jpg" },
  { key: "trailers", img: "https://kataco.ro/wp-content/uploads/2022/08/13358899_semiremorca-o2-piacenza_1.jpg" },
  { key: "forklifts", img: "https://kataco.ro/wp-content/uploads/2022/05/12011530_jcb-803-miniexcavator_4.jpg" },
];

const SERVICES = [
  { icon: ShieldCheck, t: "srvx.i1t", d: "srvx.i1d" },
  { icon: FileText, t: "srvx.i2t", d: "srvx.i2d" },
  { icon: ClipboardCheck, t: "srvx.i3t", d: "srvx.i3d" },
  { icon: Handshake, t: "srvx.i4t", d: "srvx.i4d" },
];

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.14 } }),
};

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 2, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref}>
      {val.toLocaleString("en-US")}
      {suffix}
    </span>
  );
}

function Hero() {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="grain relative min-h-screen flex items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="" fetchPriority="high" className="h-[115%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B10] via-[#0A0B10]/80 to-[#0A0B10]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10] via-transparent to-[#0A0B10]/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              data-testid="hero-kicker"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-mono-num text-xs tracking-[0.35em] text-[#4499F7] uppercase mb-8"
            >
              {t("hero.kicker")}
            </motion.p>

            <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-7xl" data-testid="hero-headline">
              {["l1", "l2", "l3"].map((k, i) => (
                <span key={k} className="block overflow-hidden pb-1">
                  <motion.span
                    custom={i}
                    variants={lineVariant}
                    initial="hidden"
                    animate="show"
                    className={`block ${k === "l3" ? "text-[#4499F7]" : "text-slate-50"}`}
                  >
                    {t(`hero.${k}`)}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              data-testid="hero-sub"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-8 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
            >
              {t("hero.sub")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/inventory"
                data-testid="hero-cta-inventory"
                className="group flex items-center gap-3 bg-[#4499F7] text-[#0A0B10] font-semibold rounded-full px-8 py-4 hover:bg-[#6FB1FA] transition-colors"
              >
                {t("hero.cta1")}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="flex items-center gap-3 border border-white/20 text-slate-100 font-semibold rounded-full px-8 py-4 hover:border-[rgba(68,153,247,0.6)] hover:text-[#4499F7] transition-colors"
              >
                {t("hero.cta2")}
              </Link>
            </motion.div>
          </div>

          <div className="hidden lg:block lg:col-span-5" style={{ perspective: 1200 }}>
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1.2, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="spotlight-card relative rounded-3xl border border-[rgba(68,153,247,0.25)] overflow-hidden shadow-[0_40px_120px_-20px_rgba(68,153,247,0.2)]"
              data-testid="hero-spotlight-card"
            >
              <img src={HERO_CARD} alt="Spotlight vehicle" className="aspect-[4/3] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10]/95 via-[#0A0B10]/25 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6" style={{ transform: "translateZ(40px)" }}>
                <p className="font-mono-num text-[11px] tracking-[0.3em] text-[#4499F7] uppercase" style={{ textShadow: "0 1px 8px rgba(10,11,16,0.9)" }}>Porsche Cayenne 3.2 V6</p>
                <p className="font-display text-2xl font-bold text-slate-50 mt-1" style={{ textShadow: "0 1px 10px rgba(10,11,16,0.9)" }}>€12 999</p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500"
      >
        <ArrowDown size={18} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  const [featured, setFeatured] = useState([]);

useEffect(() => {
  api.get("/vehicles", { params: { featured: true } })
    .then((r) => {
      const data = r.data;
      const vehicles = Array.isArray(data)
        ? data
        : Array.isArray(data?.vehicles)
          ? data.vehicles
          : [];
      setFeatured(vehicles.slice(0, 6));
    })
    .catch(() => setFeatured([]));
}, []);

  const whyItems = [
    { icon: ShieldCheck, title: t("why.i1t"), desc: t("why.i1d") },
    { icon: Globe2, title: t("why.i2t"), desc: t("why.i2d") },
    { icon: BadgePercent, title: t("why.i3t"), desc: t("why.i3d") },
    { icon: ClipboardCheck, title: t("why.i4t"), desc: t("why.i4d") },
  ];

  return (
    <div data-testid="home-page">
      <Seo title={t("seo.homeTitle")} description={t("seo.homeDesc")} />
      <Hero />
      <Marquee />

      <section data-testid="brand-banner-section" className="py-12 lg:py-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link
              to="/contact"
              data-testid="brand-banner-link"
              className="group relative block rounded-3xl overflow-hidden border border-white/10 hover:border-[rgba(68,153,247,0.45)] transition-colors duration-500 shadow-[0_30px_100px_-30px_rgba(68,153,247,0.25)]"
            >
              <img
                src="/banner.webp"
                alt="KATACO Worldwide SRL — Parc Auto: vânzări auto, acte auto, auto rulate, import și vânzare"
                loading="lazy"
                className="w-full aspect-[8/3] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
              />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="services-band" className="py-20 lg:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono-num text-xs tracking-[0.35em] text-[#4499F7] uppercase mb-4">{t("srvx.kicker")}</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50">{t("srvx.title")}</h2>
            <p className="mt-4 text-slate-400 max-w-xl">{t("srvx.sub")}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.t} delay={i * 0.08}>
                  <div data-testid={`service-highlight-${i}`} className="h-full bg-[#12141D] border border-white/10 rounded-2xl p-7 hover:border-[rgba(68,153,247,0.4)] transition-colors spotlight-card overflow-hidden">
                    <Icon size={26} className="text-[#4499F7]" />
                    <h3 className="mt-5 font-display font-semibold text-lg text-slate-50">{t(s.t)}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t(s.d)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="categories-section" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono-num text-xs tracking-[0.35em] text-[#4499F7] uppercase mb-4">KATACO</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50">{t("cats.title")}</h2>
            <p className="mt-4 text-slate-400 max-w-xl">{t("cats.sub")}</p>
          </Reveal>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.key} delay={i * 0.08}>
                <Link
                  to={`/inventory?category=${c.key}`}
                  data-testid={`category-card-${c.key}`}
                  className="group relative block rounded-2xl overflow-hidden border border-white/10 hover:border-[rgba(68,153,247,0.4)] transition-colors duration-500"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={c.img} alt={t(`cats.${c.key}`)} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10] via-[#0A0B10]/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 flex items-center justify-between">
                    <span className="font-display font-semibold text-slate-50">{t(`cats.${c.key}`)}</span>
                    <ArrowRight size={16} className="text-[#4499F7] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="featured-section" className="py-20 lg:py-28 bg-[#0D0F16] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50">{t("featured.title")}</h2>
              <p className="mt-4 text-slate-400 max-w-xl">{t("featured.sub")}</p>
            </div>
            <Link
              to="/inventory"
              data-testid="featured-view-all-link"
              className="group flex items-center gap-2 text-sm font-semibold text-[#4499F7] hover:text-[#6FB1FA] transition-colors"
            >
              {t("common.viewAll")}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {featured.map((v, i) => (
              <VehicleCard key={v.id} vehicle={v} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section data-testid="why-section" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50">{t("why.title")}</h2>
            <p className="mt-4 text-slate-400 max-w-xl">{t("why.sub")}</p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6" data-testid="why-bento-grid">
            <Reveal className="lg:col-span-7" delay={0}>
              <div className="h-full bg-[#12141D] border border-white/10 rounded-3xl p-8 lg:p-10 spotlight-card overflow-hidden">
                <ShieldCheck size={32} className="text-[#4499F7]" />
                <h3 className="mt-6 font-display text-xl sm:text-2xl font-semibold text-slate-50">{whyItems[0].title}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed max-w-md">{whyItems[0].desc}</p>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-5" delay={0.1}>
              <div className="h-full bg-[#12141D] border border-white/10 rounded-3xl p-8 lg:p-10">
                <Globe2 size={32} className="text-[#4499F7]" />
                <h3 className="mt-6 font-display text-xl sm:text-2xl font-semibold text-slate-50">{whyItems[1].title}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{whyItems[1].desc}</p>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-5" delay={0.15}>
              <div className="h-full bg-[#12141D] border border-white/10 rounded-3xl p-8 lg:p-10">
                <BadgePercent size={32} className="text-[#4499F7]" />
                <h3 className="mt-6 font-display text-xl sm:text-2xl font-semibold text-slate-50">{whyItems[2].title}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{whyItems[2].desc}</p>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-7" delay={0.2}>
              <div className="h-full bg-[#12141D] border border-white/10 rounded-3xl p-8 lg:p-10 spotlight-card overflow-hidden">
                <ClipboardCheck size={32} className="text-[#4499F7]" />
                <h3 className="mt-6 font-display text-xl sm:text-2xl font-semibold text-slate-50">{whyItems[3].title}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed max-w-md">{whyItems[3].desc}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="stats-section" className="py-16 lg:py-24 border-y border-white/5 bg-[#0D0F16]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {[
            { to: 52, suffix: "+", label: t("stats.stock"), id: "stock" },
            { to: 15, suffix: "", label: t("stats.years"), id: "years" },
            { to: 2400, suffix: "+", label: t("stats.delivered"), id: "delivered" },
            { to: 98, suffix: "%", label: t("stats.satisfaction"), id: "satisfaction" },
          ].map((s) => (
            <Reveal key={s.id} className="text-center">
              <p data-testid={`stat-${s.id}`} className="font-mono-num text-4xl lg:text-5xl font-bold text-[#4499F7]">
                <Counter to={s.to} suffix={s.suffix} />
              </p>
              <p className="mt-3 text-sm text-slate-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="cta-section" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-[rgba(68,153,247,0.2)] bg-gradient-to-br from-[#181A26] to-[#0D0F16] p-10 lg:p-20 text-center">
              <div className="absolute inset-0 bg-[radial-gradient(700px_circle_at_50%_-20%,rgba(68,153,247,0.12),transparent_60%)]" />
              <div className="relative">
                <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-bold tracking-tight text-slate-50">{t("cta.title")}</h2>
                <p className="mt-5 text-slate-400 max-w-2xl mx-auto">{t("cta.sub")}</p>
                <div className="mt-10 flex flex-wrap justify-center gap-4">
                  <Link
                    to="/contact"
                    data-testid="cta-enquiry-btn"
                    className="bg-[#4499F7] text-[#0A0B10] font-semibold rounded-full px-8 py-4 hover:bg-[#6FB1FA] transition-colors"
                  >
                    {t("cta.btn")}
                  </Link>
                  <a
                    href="tel:+40723250260"
                    data-testid="cta-call-btn"
                    className="flex items-center gap-2 border border-white/20 text-slate-100 font-semibold rounded-full px-8 py-4 hover:border-[rgba(68,153,247,0.6)] hover:text-[#4499F7] transition-colors"
                  >
                    <Phone size={16} />
                    +40 723 250 260
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
